// Deep-link v2 — parità con map-config.php + applyUrlLayerState v1:
// layer flags, focus view, station, province, filtri, embed/bot/kiosk.
import { setFilters, municipalityBounds, municipalityKey, state as stationsState } from '../layers/stations.js';
import { setInsetsEnabled } from '../layers/misc.js';
import { provinceMainlandBounds, setProvinceMask } from '../layers/province-mask.js';
import { SICILY_BOUNDS } from '../lib/config.js';

// Province con isole lontane: la loro rete include isole a ~150-200 km (Pelagie
// per Agrigento, Pantelleria per Trapani). Zoomare sulla provincia taglierebbe le
// isole; fittare tutto rimpicciolirebbe la Sicilia. Sui rendering bot/embed teniamo
// quindi la Sicilia intera + un riquadro dedicato all'isola (come "Mostra isole").
const FAR_ISLAND_INSET = { AG: 'pelagie', TP: 'pantelleria' };

// nome-URL v1 → id toggle v2 (il change applica il layer)
const FLAG_TO_TOGGLE = {
    markers: 'chkMarkers',
    stazioni: 'chkMarkers',
    stations: 'chkMarkers',
    radar: 'chkRadar',
    satellite: 'chkSatellite',
    sat: 'chkSatellite',
    nubi: 'chkSatellite',
    clouds: 'chkSatellite',
    lightning: 'chkLightning',
    fulmini: 'chkLightning',
    fulminazioni: 'chkLightning',
    blitzortung: 'chkLightning',
    temporali: 'chkTemporali',
    storms: 'chkTemporali',
    celle: 'chkTemporali',
    grandine: 'chkHail',
    hail: 'chkHail',
    interpol: 'chkInterpol',
    interpolazione: 'chkInterpol',
    color: 'chkInterpol',
    colore: 'chkInterpol',
    wslam: 'chkWslam',
    mare: 'chkWslam',
    sea: 'chkWslam',
    forecast: 'chkTimeline',
    previsioni: 'chkTimeline',
    alerts: 'risk-alerts',
    allerte: 'risk-alerts',
    wildfire_alerts: 'risk-wildfireAlerts',
    wildfireAlerts: 'risk-wildfireAlerts',
    wildfirealerts: 'risk-wildfireAlerts',
    allerte_incendi: 'risk-wildfireAlerts',
    firms: 'risk-firms',
    incendi: 'risk-firms',
    fires: 'risk-firms',
    water_resources: 'risk-waterResources',
    waterResources: 'risk-waterResources',
    waterresources: 'risk-waterResources',
    risorse_idriche: 'risk-waterResources',
    invasi: 'risk-waterResources',
    riserve: 'risk-waterResources',
    webcam: 'me-webcam',
    webcams: 'me-webcam',
    cam: 'me-webcam',
    boe: 'me-boe',
    buoys: 'me-boe',
    mobile: 'me-mobile',
    ndbc: 'me-ndbcShips',
    noaa: 'me-ndbcShips',
    navi_noaa: 'me-ndbcShips',
    ships: 'me-ndbcShips',
    navi: 'me-ndbcShips',
    ascat: 'me-ascat',
    s1wind: 'me-s1wind',
    sentinel1: 'me-s1wind',
    wind: 'chkWind',
    vento: 'chkWind',
    quakes: 'drv-quakes',
    terremoti: 'drv-quakes',
    pollen: 'drv-pollen',
    air: 'drv-air',
    qualita_aria: 'drv-air',
    air_obs: 'drv-airObs',
    aria_misurata: 'drv-airObs',
    rainreports: 'drv-rainReports',
    rain_reports: 'drv-rainReports',
    traffic: 'drv-territory',
    territory: 'drv-territory',
    'rain-reports': 'drv-rainReports', // la webapp usa ?layer=rain-reports (trattino)
    dust: 'ovl-dust',
    fwi: 'ovl-fwi',
    solar: 'ovl-solar',
    waves: 'ovl-waves',
    onde: 'ovl-waves',
    sst: 'ovl-sst',
    temperatura_mare: 'ovl-sst',
    'temperatura-mare': 'ovl-sst',
    temp_mare: 'ovl-sst',
    timeline: 'chkTimeline',
    timeline_meteo: 'chkTimeline',
    rain_accum: 'ana-rainAccum',
    rainAccum: 'ana-rainAccum',
    rainaccum: 'ana-rainAccum',
    temp_variation: 'ana-tempVariation',
    temperature_variation: 'ana-tempVariation',
    variazione_temperatura: 'ana-tempVariation',
};

const PROVINCE_CENTERS = { PA: [13.3615, 38.1157], TP: [12.5715, 37.8338], AG: [13.5747, 37.3123], CL: [14.0629, 37.4901], EN: [14.2798, 37.5674], CT: [15.0873, 37.5027], ME: [15.5540, 38.1938], RG: [14.7306, 36.9250], SR: [15.2866, 37.0755], RC: [15.6613, 38.1105] };

function setToggle(id, on) {
    const c = document.getElementById(id);
    if (c && c.checked !== on) { c.checked = on; c.dispatchEvent(new Event('change')); }
}

export function applyDeepLink(map, url) {
    const q = url.raw;
    const truthy = (v) => ['1', 'true', 'yes', 'on'].includes(String(v || '').toLowerCase());

    // --- embed / bot / kiosk fulmini (wsForceLightningOnly v1) ---------------
    const embed = truthy(q.get('embed'));
    const bot = truthy(q.get('bot'));
    const lightningFlag = ['lightning', 'fulmini', 'fulminazioni', 'blitzortung']
        .some(key => truthy(q.get(key)));
    if (embed || bot) {
        document.documentElement.classList.add('wsv2-embed');
        if (embed && truthy(q.get('playback'))) document.documentElement.classList.add('wsv2-embed-playback');
        if (bot) {
            document.documentElement.classList.add('wsv2-bot');
            // Screenshot bot: TUTTE le stazioni, niente declutter (v1 all-bot).
            stationsState.botMode = true;
        }
        if (lightningFlag) {
            // kiosk "solo fulmini": stazioni spente, solo il layer fulmini
            document.documentElement.classList.add('wsv2-lightning-only');
            setToggle('chkMarkers', false);
            setToggle('chkLightning', true);
            return; // niente altro in kiosk
        }
    }

    // --- layers= / only= (lista) + flag singoli -------------------------------
    // layers= (bot, CSV) · only= (focus) · layer= (webapp, singolare)
    const tokens = (q.get('layers') || q.get('only') || q.get('layer') || '').split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    const focusRequested = [];
    for (const [flag, toggle] of Object.entries(FLAG_TO_TOGGLE)) {
        const inList = tokens.includes(flag);
        const asParam = truthy(q.get(flag));
        if (inList || asParam) {
            if (toggle === 'ana-rainAccum') {
                const hours = Number(q.get('rain_accum_hours') || q.get('hours') || 24);
                const value = `rain${[24,48,72,96,120].includes(hours) ? hours : 24}h`;
                document.dispatchEvent(new CustomEvent('wsv2:rain-window-select', { detail: { value } }));
                setToggle('chkMarkers', true);
            } else setToggle(toggle, true);
            focusRequested.push(flag);
        }
    }
    // Vista forzata: qualunque layer richiesto esplicitamente via URL diventa
    // il contenuto principale. I marker standard si spengono, salvo richiesta
    // esplicita con markers=1 o apertura diretta di una stazione. Prima questo
    // accadeva solo per alcuni nowcast: ?air=1, ?sst=1 ecc. attivavano il layer
    // ma lasciavano sopra la mappa di default, dando l'impressione che il link
    // dedicato fosse stato ignorato.
    const markerFlags = ['markers', 'stazioni', 'stations'];
    const markerDependentFlags = ['wind', 'vento', 'timeline', 'timeline_meteo', 'forecast', 'previsioni', 'rain_accum', 'rainAccum', 'rainaccum'];
    const markersRequested = markerFlags.some(key => tokens.includes(key) || truthy(q.get(key)));
    const hasStandaloneLayer = focusRequested.some(flag => !markerFlags.includes(flag) && !markerDependentFlags.includes(flag));
    if (hasStandaloneLayer && !markersRequested && !q.get('station') && !q.get('stazione')) {
        setToggle('chkMarkers', false);
    }
    // map= (bot): color=campo interpolato · radar=radar acceso · markers/text=puntuale (default)
    const mapMode = (q.get('map') || '').toLowerCase();
    if (mapMode === 'color') setToggle('chkInterpol', true);
    else if (mapMode === 'radar') {
        setToggle('chkRadar', true);
        if (!markersRequested && !q.get('station') && !q.get('stazione')) setToggle('chkMarkers', false);
    }

    // --- filtri ---------------------------------------------------------------
    const num = (k) => { const v = parseFloat(q.get(k)); return Number.isFinite(v) ? v : null; };
    const patch = {};
    const altMin = num('quota_min') ?? num('altitude_min') ?? num('altitudine');
    const altMax = num('quota_max') ?? num('altitude_max');
    const valMin = num('valore_min') ?? num('value_min');
    const valMax = num('valore_max') ?? num('value_max');
    if (altMin != null) patch.altMin = altMin;
    if (altMax != null) patch.altMax = altMax;
    if (valMin != null) patch.valMin = valMin;
    if (valMax != null) patch.valMax = valMax;
    // Comune (bot "comune X"): filtra alle sole stazioni del comune (PiP) e
    // inquadra i suoi confini — parità con wsIsRowInActiveProvince/municipalityBounds v1.
    const comuneRaw = String(q.get('comune') || q.get('municipality') || q.get('city') || '').trim();
    if (comuneRaw) patch.municipality = municipalityKey(comuneRaw);
    const prov = String(q.get('province') || q.get('view') || '').toUpperCase();
    if (PROVINCE_CENTERS[prov]) {
        patch.province = prov;
        // Ogni provincia si zooma sul suo centro (parità col click su AG/TP/… sul
        // sito). AG e TP hanno isole lontane (Pelagie/Pantelleria) fuori inquadratura:
        // NON allarghiamo più a tutta la Sicilia — l'isola finisce nel riquadro
        // (inset), la mappa principale resta zoomata sull'entroterra provinciale.
        // lat/comune espliciti vincono.
        if (url.lat == null && !comuneRaw) {
            provinceMainlandBounds(prov)
                .then(bounds => {
                    if (bounds) {
                        // L'inquadratura segue forma e rapporto d'aspetto reali
                        // della provincia. Nel layout bot lasciamo più margine a
                        // titolo, legenda e inset delle isole.
                        const padding = (embed || bot)
                            ? { top: 72, right: 86, bottom: 104, left: 86 }
                            : 44;
                        map.fitBounds(bounds, { padding, maxZoom: 10.5, animate: false });
                    } else {
                        map.jumpTo({ center: PROVINCE_CENTERS[prov], zoom: 10 });
                    }
                })
                .catch(() => map.jumpTo({ center: PROVINCE_CENTERS[prov], zoom: 10 }));
        }
        // Oscura tutto fuori provincia, ESATTAMENTE come il click sul bottone
        // provincia (panel.js:513). Prima il deeplink filtrava+zoomava soltanto,
        // senza il velo → gli screenshot bot "live <provincia>" (e ogni link
        // ?province=) uscivano zoomati ma senza maschera. Solo province: se c'è
        // un comune esplicito la provincia è solo il genitore del filtro comunale
        // e NON si maschera (parità col click-comune del sito, che non vela).
        if (!comuneRaw) setProvinceMask(map, prov);
    }
    if (comuneRaw && url.lat == null) {
        municipalityBounds(comuneRaw)
            .then(b => { if (b) map.fitBounds(b, { padding: 40, maxZoom: 13, animate: false }); })
            .catch(() => {});
    }
    // Vista Sicilia intera (bot/SEO: view=sicilia o location=sicilia e sinonimi).
    const SICILY_WORDS = ['sicilia', 'sici', 'regionale', 'regione', 'isola'];
    const viewVal = String(q.get('view') || '').trim().toLowerCase();
    const locVal = String(q.get('location') || '').trim().toLowerCase();
    if ((SICILY_WORDS.includes(viewVal) || SICILY_WORDS.includes(locVal)) && url.lat == null) {
        map.fitBounds(SICILY_BOUNDS, { padding: 20, animate: false });
    }
    if (Object.keys(patch).length) setFilters(patch);

    // Riquadri isola sugli screenshot bot (insets=1): SOLO per le province con
    // un'isola lontana fuori dall'inquadratura (AG→Pelagie, TP→Pantelleria), dove
    // servono a mostrarne le stazioni. Sulla livemap Sicilia intera e sulle altre
    // province NON si mostrano (richiesta Giuseppe: niente isole nella livemap).
    const insetProv = FAR_ISLAND_INSET[prov];
    if ((embed || bot) && truthy(q.get('insets')) && insetProv && url.lat == null && !comuneRaw) {
        document.documentElement.classList.add('wsv2-force-insets');
        setInsetsEnabled(true, [insetProv]);
    }

    // --- opzioni marker --------------------------------------------------------
    if (q.has('quota_labels') || q.has('altitude_labels')) setToggle('chkAltitudeLabels', truthy(q.get('quota_labels') ?? q.get('altitude_labels')));
    if (q.has('marker_extremes') || q.has('max_min_markers')) setToggle('chkMarkerExtremes', truthy(q.get('marker_extremes') ?? q.get('max_min_markers')));
    // Toggle espliciti (SEO/bot passano anche =0 per una mappa "pulita"):
    // markers è default ON → senza questo un markers=0 resterebbe ignorato.
    if (q.has('markers') || q.has('stazioni')) setToggle('chkMarkers', truthy(q.get('markers') ?? q.get('stazioni')));
    if (q.has('wind')) setToggle('chkWind', truthy(q.get('wind')));
    if (q.has('interpol')) setToggle('chkInterpol', truthy(q.get('interpol')));
    // Base satellitare (bot/SEO: baseSat=0 → base OSM, baseSat=1 → satellite).
    if (q.has('baseSat') || q.has('base_sat') || q.has('basemap_satellite')) {
        setToggle('chkBaseSat', truthy(q.get('baseSat') ?? q.get('base_sat') ?? q.get('basemap_satellite')));
    }

    // --- ricerca precompilata (?cerca= dalla landing app bot) ------------------
    const cerca = String(q.get('cerca') || '').trim();
    if (cerca && !embed && !bot) {
        const openSearch = () => {
            const input = document.getElementById('wsv2-search-input');
            if (!input) return;
            input.value = cerca;
            input.focus();                                   // innesca loadComuni (once)
            input.dispatchEvent(new Event('input', { bubbles: true })); // popola i risultati
        };
        // dopo il boot: la UI di ricerca è pronta e le stazioni indicizzate
        setTimeout(openSearch, 400);
    }

    // --- stazione richiesta ------------------------------------------------------
    // Interattivo: fly + card. Embed/bot (widget e screenshot): SOLO quella
    // stazione, inquadratura stretta senza popup (parità v1).
    if (url.station) {
        const id = String(url.station);
        if (embed || bot) stationsState.embedStationId = id;
        const focus = (e) => {
            const rec = (e.detail.records || []).find(r => r.id === id);
            if (!rec) return;
            document.removeEventListener('wsv2:stations', focus);
            if (embed || bot) {
                map.jumpTo({ center: [rec.lon, rec.lat], zoom: 13 });
            } else {
                map.flyTo({ center: [rec.lon, rec.lat], zoom: Math.max(map.getZoom(), 12) });
                map.once('moveend', () => document.dispatchEvent(new CustomEvent('wsv2:station-click', { detail: { rec } })));
            }
        };
        document.addEventListener('wsv2:stations', focus);
    }

    // --- noclick (embed app) ---------------------------------------------------
    if (truthy(q.get('noclick'))) document.documentElement.classList.add('wsv2-noclick');
}
